import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signup,
  updateUserInfo,
  updateUserPass,
  assignStoreToUser,
} from "../../../components/users/functions";
import { getStores } from "../../../components/stores/functions";
import {
  birthMask,
  cpfMask,
  demasker,
  formatarData,
  isNumeric,
  parseDateString,
  phoneMask,
  validateCPF,
} from "../../../components/utils/functions";
import {
  getVisibleGradeFields,
  renderApplicableGradeValue,
} from "../../../components/utils/serialGradeFields";
import ToastContext from "../../../context/toastProvider";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobileFormField from "../../ui/MobileFormField";
import MobileConfirm from "../../ui/MobileConfirm";

const inputClass =
  "w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha";
const primaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50";
const secondaryBtn =
  "inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg border border-primary/50 px-4 text-sm font-medium text-laranjinha/80 hover:bg-monao disabled:opacity-50";

function SheetDetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

export function MobileUserSerialSheet({ open, onClose, serial, userData }) {
  const navigate = useNavigate();

  if (!serial) return null;

  const productRef = serial?.product || serial?.productData;
  const visibleGrades = getVisibleGradeFields(serial, productRef);

  const navigateToProduct = () => {
    const productId =
      typeof serial?.product === "object" ? serial?.product?._id : serial?.product;
    if (!productId) return;
    navigate(`/manafix/products/${productId}`, {
      state: {
        product: productId,
        origin: "users",
        user: userData,
      },
    });
    onClose();
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Detalhe do serial">
      <div className="space-y-4">
        <div>
          <p className="text-xs text-laranjinha/60">Serial</p>
          <p className="text-sm font-medium text-laranjinha">
            {serial?.serialNumber ?? serial?._id}
          </p>
        </div>

        {serial?.gradableModel ? (
          <div className="rounded-lg border border-primary/30 p-3">
            <h3 className="mb-2 text-sm font-semibold text-laranjinha">Modelo</h3>
            <SheetDetailRow label="ID" value={serial.gradableModel._id} />
            <SheetDetailRow label="Jogo" value={serial.gradableModel.game} />
            <SheetDetailRow label="Nome" value={serial.gradableModel.name} />
            <SheetDetailRow label="Edição" value={serial.gradableModel.edition} />
            <SheetDetailRow label="Ano" value={serial.gradableModel.year} />
            <SheetDetailRow label="Idioma" value={serial.gradableModel.language} />
            <SheetDetailRow label="Numeração" value={serial.gradableModel.numbering} />
            <SheetDetailRow label="Raridade" value={serial.gradableModel.rarity} />
          </div>
        ) : null}

        <div className="rounded-lg border border-primary/30 p-3">
          <h3 className="mb-2 text-sm font-semibold text-laranjinha">Notas</h3>
          <SheetDetailRow label="ID" value={serial?._id} />
          {visibleGrades.generalScore ? (
            <SheetDetailRow
              label={visibleGrades.generalScore.label}
              value={renderApplicableGradeValue(visibleGrades.generalScore.value)}
            />
          ) : null}
          {visibleGrades.subgrades.map((field) => (
            <SheetDetailRow
              key={field.key}
              label={field.label}
              value={renderApplicableGradeValue(field.value)}
            />
          ))}
          {visibleGrades.autographNote ? (
            <SheetDetailRow
              label={visibleGrades.autographNote.label}
              value={renderApplicableGradeValue(visibleGrades.autographNote.value)}
            />
          ) : null}
        </div>

        {serial?.product ? (
          <div className="rounded-lg border border-primary/30 p-3">
            <h3 className="mb-2 text-sm font-semibold text-laranjinha">Pedido</h3>
            <SheetDetailRow
              label="ID"
              value={
                typeof serial.product === "object" ? serial.product._id : serial.product
              }
            />
            <button
              type="button"
              onClick={navigateToProduct}
              className={`mt-2 ${primaryBtn}`}
            >
              Ver pedido
            </button>
          </div>
        ) : null}
      </div>
    </MobileFilterSheet>
  );
}

export function MobileUserEditSheet({

  open,
  onClose,
  user,
  userData,
  onSuccess,
}) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [docType, setDocType] = useState("CPF");
  const [docNumber, setDocNumber] = useState("");
  const [birth, setBirth] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !userData) return;
    setName(userData.name ?? "");
    setEmail(userData.email ?? "");
    setPhone(phoneMask(userData.phone) ?? "");
    setDocType(userData.documentType || "CPF");
    setDocNumber(
      userData.documentType === "CPF"
        ? cpfMask(userData.documentNumber)
        : userData.documentNumber ?? ""
    );
    setBirth(formatarData(userData.birth) ?? "");
    setIsVerified(Boolean(userData.verified));
    setIsAdmin(userData.roles?.includes("admin") ?? false);
    setIsPartner(userData.roles?.includes("partner") ?? false);
  }, [open, userData]);

  const buildChanges = () => {
    const changes = {};
    if (name !== userData.name) changes.name = name;
    if (email?.toLowerCase() !== userData.email) changes.email = email?.toLowerCase();
    if (demasker(phone) !== userData.phone) changes.phone = demasker(phone);
    if (docType !== userData.documentType) changes.documentType = docType;
    if (demasker(docNumber) !== userData.documentNumber) {
      changes.documentNumber = demasker(docNumber);
    }
    if (birth !== formatarData(userData.birth)) {
      changes.birth = parseDateString(birth);
    }
    if (isVerified !== userData.verified) changes.verified = isVerified;

    const roles = ["customer"];
    if (isAdmin) roles.push("admin");
    if (isPartner) roles.push("partner");

    const sameRoles =
      roles.length === userData.roles?.length &&
      roles.every((role) => userData.roles.includes(role));
    if (!sameRoles) changes.roles = roles;

    return changes;
  };

  const handleSave = async () => {
    if (birth.length !== 10) {
      badToast("Data de nascimento inválida!");
      return;
    }
    const body = buildChanges();
    if (!Object.keys(body).length) {
      badToast("Nenhuma alteração feita");
      return;
    }
    setBusy(true);
    const data = await updateUserInfo(user?.token, userData._id, body);
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Usuário atualizado");
      onSuccess();
      onClose();
      return;
    }
    badToast(data?.message ?? "Erro ao atualizar usuário");
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Editar usuário">
      <div className="space-y-3">
        <MobileFormField label="Nome">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="E-mail">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Telefone">
          <input
            type="text"
            value={phone}
            onChange={(e) => {
              const value = demasker(e.target.value);
              if (!value || isNumeric(value)) setPhone(phoneMask(value));
            }}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Tipo documento">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className={inputClass}
          >
            <option value="CPF">CPF</option>
            <option value="other">Outro</option>
          </select>
        </MobileFormField>
        <MobileFormField label="Nº documento">
          <input
            type="text"
            value={docNumber}
            onChange={(e) => {
              const value = demasker(e.target.value);
              if (!value || isNumeric(value)) {
                setDocNumber(docType === "CPF" ? cpfMask(value) : value);
              }
            }}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Nascimento">
          <input
            type="text"
            value={birth}
            onChange={(e) => {
              const value = demasker(e.target.value);
              if (!value || isNumeric(value)) setBirth(birthMask(value));
            }}
            className={inputClass}
          />
        </MobileFormField>
        <label className="flex min-h-[44px] items-center gap-3">
          <input
            type="checkbox"
            checked={isVerified}
            onChange={() => setIsVerified(!isVerified)}
            className="h-5 w-5"
          />
          <span className="text-sm text-laranjinha">Verificado</span>
        </label>
        <label className="flex min-h-[44px] items-center gap-3">
          <input
            type="checkbox"
            checked={isAdmin}
            onChange={() => setIsAdmin(!isAdmin)}
            className="h-5 w-5"
          />
          <span className="text-sm text-laranjinha">Admin</span>
        </label>
        <label className="flex min-h-[44px] items-center gap-3">
          <input
            type="checkbox"
            checked={isPartner}
            onChange={() => setIsPartner(!isPartner)}
            className="h-5 w-5"
          />
          <span className="text-sm text-laranjinha">Parceiro</span>
        </label>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className={primaryBtn}
          >
            Salvar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

export function MobileUserPassSheet({ open, onClose, user, userData }) {
  const { goodToast, badToast } = useContext(ToastContext);
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPass("");
    setConfirm("");
  }, [open]);

  const generatePass = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generated = "";
    for (let i = 0; i < 8; i += 1) {
      generated += charset[Math.floor(Math.random() * charset.length)];
    }
    setPass(generated);
    setConfirm(generated);
  };

  const handleSave = async () => {
    if (pass.length < 8) {
      badToast("Senha muito curta!");
      return;
    }
    if (pass !== confirm) {
      badToast("Senhas diferem!");
      return;
    }
    setBusy(true);
    const data = await updateUserPass(user?.token, userData._id, { password: pass });
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Senha alterada!");
      onClose();
      return;
    }
    badToast("Nenhuma alteração feita");
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Alterar senha">
      <div className="space-y-3">
        <MobileFormField label="Nova senha">
          <input
            type="text"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <MobileFormField label="Confirmar senha">
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
          />
        </MobileFormField>
        <button type="button" onClick={generatePass} className={secondaryBtn}>
          Gerar senha
        </button>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className={primaryBtn}
          >
            Salvar
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

export function MobileUserStoreSheet({
  open,
  onClose,
  user,
  userData,
  onSuccess,
}) {
  const { goodToast, badToast } = useContext(ToastContext);
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStore("");
    getStores(user?.token).then((data) => {
      if (data?.type === "success") {
        setStores(data.data ?? []);
      }
    });
  }, [open, user?.token]);

  const handleSave = async () => {
    if (!store) return;
    setBusy(true);
    const data = await assignStoreToUser(user?.token, userData._id, { store });
    setBusy(false);
    if (data?.type === "success") {
      goodToast("Loja atribuída ao usuário!");
      onSuccess();
      onClose();
      return;
    }
    badToast("Nenhuma alteração feita");
  };

  return (
    <MobileFilterSheet open={open} onClose={onClose} title="Gerenciar parceria">
      <div className="space-y-3">
        <p className="text-sm text-laranjinha/70">
          Loja atual: <strong>{userData?.store?.name ?? "Nenhuma"}</strong>
        </p>
        <MobileFormField label="Atribuir loja">
          <select
            value={store}
            onChange={(e) => setStore(e.target.value)}
            className={inputClass}
          >
            <option value="">Selecionar</option>
            {stores.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </select>
        </MobileFormField>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className={secondaryBtn}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!store || busy}
            className={primaryBtn}
          >
            Atribuir
          </button>
        </div>
      </div>
    </MobileFilterSheet>
  );
}

export function MobileCreateUserSheet({ open, onClose, user, onCreated }) {
  const { goodToast, badToast } = useContext(ToastContext);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [birth, setBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [docType, setDocType] = useState("CPF");
  const [loading, setLoading] = useState(false);
  const [cpfModal, setCpfModal] = useState(false);
  const [noCEP, setNoCEP] = useState(false);

  const [addressName, setAddressName] = useState("");
  const [cep, setCep] = useState("");
  const [street, setStreet] = useState("");
  const [district, setDistrict] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [reference, setReference] = useState("");
  const [complement, setComplement] = useState("");

  const resetForm = () => {
    setName("");
    setEmail("");
    setBirth("");
    setPhone("");
    setDocNumber("");
    setDocType("CPF");
    setAddressName("");
    setCep("");
    setStreet("");
    setDistrict("");
    setHouseNumber("");
    setCity("");
    setState("");
    setReference("");
    setComplement("");
    setNoCEP(false);
    setCpfModal(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const setFormattedBirth = (value) => {
    let formatted = value.replace(/\D/g, "");
    if (formatted.length > 2) {
      formatted = `${formatted.substring(0, 2)}/${formatted.substring(2)}`;
    }
    if (formatted.length > 5) {
      formatted = `${formatted.substring(0, 5)}/${formatted.substring(5)}`;
    }
    if (formatted.length <= 10) {
      setBirth(formatted);
    }
  };

  const searchCEP = async (postal) => {
    try {
      setLoading(true);
      const response = await fetch(`https://viacep.com.br/ws/${postal}/json/`);
      if (response.ok) {
        setNoCEP(false);
        const data = await response.json();
        if (data.erro) {
          setNoCEP(true);
          return;
        }
        setStreet(data.logradouro);
        setDistrict(data.bairro);
        setCity(data.localidade);
        setState(data.uf);
      } else {
        setNoCEP(true);
      }
    } catch {
      setNoCEP(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCep = (value) => {
    const tempValue = value?.split("-")?.join("");
    if (isNumeric(tempValue)) {
      setCep(tempValue);
      if (tempValue.length === 8) {
        searchCEP(tempValue);
      }
    }
  };

  const handleSubmit = async (validate = true) => {
    if (!name) return badToast("Insira um nome");
    if (!email?.includes("@") || !email?.includes(".")) return badToast("Email inválido!");
    if (birth.length !== 10) return badToast("Data de nascimento inválida!");
    if (phone.length < 8) return badToast("Telefone inválido");
    if (!addressName) return badToast("Insira um nome para o endereço");
    if (!cep) return badToast("Insira um CEP para o endereço");
    if (!street) return badToast("Insira a rua do endereço");
    if (!district) return badToast("Insira o bairro do endereço");
    if (!city) return badToast("Insira a cidade do endereço");
    if (!state) return badToast("Insira o Estado do endereço");
    if (!houseNumber) return badToast("Insira o número do endereço");
    if (!validateCPF(docNumber) && validate) {
      setCpfModal(true);
      return;
    }

    setLoading(true);
    const [day, month, year] = birth.split("/");
    const birthDate = new Date(year, month - 1, day);
    const registerBody = {
      name,
      birth: birthDate,
      email: email?.toLowerCase(),
      phone: demasker(phone),
      documentNumber: demasker(docNumber),
      documentType: docType,
      addressName,
      postalCode: cep,
      street,
      district,
      city,
      state,
      houseNumber,
      reference,
      complement,
      receiver: addressName,
      createAddress: true,
    };

    const data = await signup(user?.token, registerBody, false);
    setLoading(false);
    if (data?.type === "success") {
      goodToast("Novo usuário cadastrado!");
      onCreated(data?.data?._id);
      handleClose();
      return;
    }
    badToast(data?.message || "Erro ao cadastrar usuário");
  };

  const disableButton =
    loading ||
    !name ||
    !email ||
    !birth ||
    !phone ||
    !docNumber ||
    !addressName ||
    !cep ||
    !street ||
    !district ||
    !city ||
    !state ||
    !houseNumber;

  return (
    <>
      <MobileFilterSheet open={open} onClose={handleClose} title="Cadastrar usuário">
        <div className="space-y-3">
          <MobileFormField label="Nome">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="E-mail">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Nascimento">
            <input
              type="text"
              value={birth}
              onChange={(e) => setFormattedBirth(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Telefone">
            <input
              type="text"
              value={phone}
              onChange={(e) => {
                const value = demasker(e.target.value);
                if (!value || isNumeric(value)) setPhone(phoneMask(value));
              }}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Tipo documento">
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className={inputClass}
            >
              <option value="CPF">CPF</option>
              <option value="other">Outro</option>
            </select>
          </MobileFormField>
          <MobileFormField label="Nº documento">
            <input
              type="text"
              value={docNumber}
              onChange={(e) => {
                const value = demasker(e.target.value);
                if (!value) {
                  setDocNumber("");
                  return;
                }
                if (docType === "CPF") {
                  setDocNumber(cpfMask(value));
                } else if (isNumeric(value)) {
                  setDocNumber(value);
                }
              }}
              className={inputClass}
            />
          </MobileFormField>

          <h3 className="pt-2 text-sm font-semibold text-laranjinha/80">Endereço</h3>
          <MobileFormField label="Nome do recebedor">
            <input
              type="text"
              value={addressName}
              onChange={(e) => setAddressName(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="CEP">
            <input
              type="text"
              value={cep}
              onChange={(e) => handleCep(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          {noCEP ? (
            <p className="text-xs text-red-500">CEP não encontrado!</p>
          ) : null}
          <MobileFormField label="Logradouro">
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Número">
            <input
              type="text"
              value={houseNumber}
              onChange={(e) => setHouseNumber(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Complemento">
            <input
              type="text"
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Bairro">
            <input
              type="text"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Cidade">
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Estado">
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>
          <MobileFormField label="Ponto de referência">
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className={inputClass}
            />
          </MobileFormField>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} className={secondaryBtn}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(true)}
              disabled={disableButton}
              className={primaryBtn}
            >
              Cadastrar
            </button>
          </div>
        </div>
      </MobileFilterSheet>

      <MobileConfirm
        open={cpfModal}
        title="CPF inválido"
        message="O CPF digitado é inválido. Deseja prosseguir mesmo assim?"
        onCancel={() => setCpfModal(false)}
        onConfirm={() => {
          setCpfModal(false);
          handleSubmit(false);
        }}
      />
    </>
  );
}
